'use client';

import React from 'react';
import { useTranslations } from 'use-intl';
import Checkbox from '@/components/atoms/checkbox';
import { classnames } from '@/utils/classnames/classnames';
import type { QuickFilterPillsProps } from '@/components/molecules/quick-filter-pills/types';

// EWM-686 — boolean quick filters rendered as CHECKBOXES inline in the
// FilterToolbar row, alongside the Brand dropdown.
//
// Shares `QuickFilterPillSpec` and the `onToggle` contract with the sibling
// `quick-filter-pills` molecule, so both are driven by the same view-model
// bindings and URL plumbing — only the presentation differs. The ticket asks
// specifically for "a checkbox filter … next to brands filter dropdown"
// (EWM-686), hence the checkbox rather than the EWM-185 pill.
//
// Pure list renderer: no internal state. The caller owns URL state and passes
// `active` + `onToggle`. Renders nothing when there are no filters to show.
const QuickFilterCheckboxes = ({ pills, onToggle, className = '' }: QuickFilterPillsProps) => {
  const translate = useTranslations();

  if (pills.length === 0) return null;

  return (
    <div
      role="group"
      aria-label={translate('product.quick-filters')}
      className={classnames('inline-flex flex-wrap items-center gap-4', className)}
    >
      {pills.map((pill) => (
        <Checkbox
          key={pill.id}
          size="sm"
          label={pill.label}
          checked={pill.active}
          onChecked={(next) => onToggle(pill.id, next)}
          containerClassName="text-13 text-gray-700"
        />
      ))}
    </div>
  );
};

export default QuickFilterCheckboxes;
